import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useOnestockIntegrationUpdateMutation from "@babackapp/private-api-client/client/use-update-onestock-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";

const formSchema = z.object({
  activated: z.boolean(),
  siteId: z.string(),
  userId: z.string(),
  password: z.string(),
  url: z.string(),
});

const OneStockIntegration = () => {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { activated, siteId, userId, password, url } =
    shop.integrations.oneStock;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { activated, siteId, userId, password, url },
  });

  const { mutateAsync: updateOneStockIntegration } =
    useOnestockIntegrationUpdateMutation(shop);

  const onSubmit = form.handleSubmit((data) => {
    updateOneStockIntegration(data).then(() => form.reset(data));
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col justify-start gap-2">
        <FormField
          control={form.control}
          name="siteId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.oneStock.siteId")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex gap-4 w-full">
          <FormField
            control={form.control}
            name="userId"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {t("settings.integrations.thirdpart.oneStock.userId")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {t("settings.integrations.thirdpart.oneStock.password")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.oneStock.url")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={!form.formState.isDirty}>
          {t("misc.save")}
        </Button>
      </form>
    </Form>
  );
};

export function OnestockIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: updateActivation } =
    useOnestockIntegrationUpdateMutation(shop);

  const onActivationUpdate = useCallback(
    (activated: boolean) => {
      updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  if (!shop || !shop.plan.oneStock) return null;

  return (
    <IntegrationPreview
      title="OneStock"
      description={t("settings.integrations.thirdpart.oneStock.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.oneStock.activated}
      logo="/integrations/onestock.svg"
      seeMoreUrl="https://onestock-retail.com/"
    >
      <OneStockIntegration />
    </IntegrationPreview>
  );
}
